import { useEffect, useMemo, useState } from 'react';
import { USE_MOCKS, apiGet, apiPatch, apiPost, apiDelete } from '../api/client';
import { useBrand } from '../brand/BrandProvider';
import {
  Check,
  Laptop,
  Smartphone,
  Monitor,
  Tablet,
  ShieldCheck,
  Key,
  AlertTriangle,
  QrCode,
  Copy,
  Printer,
  Plus,
  CircleDot,
  ShieldOff,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardHeader } from '../components/Card';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { Tabs } from '../components/Tabs';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/cn';
import type {
  DeviceSession,
  MfaFactor,
  MfaKind,
} from '../mocks/clientBatch';
import {
  useMyActiveSessions,
  useMy2faFactors,
  useMy2faRecoveryCodes,
  revokeMySession,
  revokeAllMySessions,
  SESSION_CAP_DEFAULT,
} from '../api/platform';
import { useSession } from '../stores/session';

export function Profile() {
  const { data: myActiveSessions } = useMyActiveSessions();
  // Session state lifted out so we can show a live count in the tab badge
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const activeSessionCount = sessions.filter((s) => !s.revokedAt).length;

  // Seed the editable session list once the fetched sessions land.
  useEffect(() => {
    if (!myActiveSessions) return;
    setSessions(myActiveSessions.map((s) => ({ ...s })));
  }, [myActiveSessions]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Profile"
        subtitle="Personal information, security, sessions, and notifications."
      />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'personal', label: 'Personal', content: <PersonalTab /> },
            { id: 'security', label: 'Security', content: <SecurityTab /> },
            {
              id: 'sessions',
              label: 'Sessions',
              content: <SessionsTab sessions={sessions} setSessions={setSessions} />,
              count: activeSessionCount,
            },
            { id: 'notifications', label: 'Notifications', content: <NotificationsTab /> },
          ]}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function PersonalTab() {
  const user = useSession((s) => s.user);
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState((user as { phone?: string } | null)?.phone ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setSaved(false);
    try {
      if (!USE_MOCKS) {
        const patch: { name: string; contactPhone?: string } = { name: name.trim() };
        if (phone && !phone.includes('X')) patch.contactPhone = phone.trim();
        await apiPatch('/me/profile', patch);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }
  function discard() {
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
  }

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Input
          label="Full name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          suffix={<Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
          hint={email ? `Verified · ${email}` : 'Verified'}
        />
        <Input
          label="Mobile (BD)"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          suffix={<Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
          hint="Verified · receives SMS OTPs and dunning alerts"
        />
        <SelectFake label="Language" value="English" options={['English', 'বাংলা']} />
        <SelectFake label="Time zone" value="Asia/Dhaka" options={['Asia/Dhaka', 'Asia/Kolkata', 'UTC']} />
      </div>
      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={discard} disabled={busy}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy || !name.trim()}>Save changes</Button>
      </div>
    </div>
  );
}

/* ── Security tab ──────────────────────────────────────────────────── */

function SecurityTab() {
  const { data: my2faFactors } = useMy2faFactors();
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Seed the editable factor list once the fetched factors land.
  useEffect(() => {
    if (!my2faFactors) return;
    setFactors(my2faFactors.map((f) => ({ ...f })));
  }, [my2faFactors]);
  const [disableOpen, setDisableOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const signOut = useSession((s) => s.signOut);

  function handleAddFactor(f: MfaFactor) {
    setFactors((prev) => [...prev, { ...f, isPrimary: prev.length === 0 }]);
  }

  function handleDisable() {
    setFactors([]);
    setDisableOpen(false);
  }

  const enabled = factors.length > 0;

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        {enabled ? (
          <TwoFaEnabledView
            factors={factors}
            onAddAnother={() => setWizardOpen(true)}
            onDisable={() => setDisableOpen(true)}
          />
        ) : (
          <TwoFaDisabledView />
        )}
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Password"
          subtitle="Keep your account secure with a strong password."
          action={<Button variant="secondary" size="sm" onClick={() => setPwOpen(true)}>Change password</Button>}
        />
      </Card>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />

      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Trusted devices"
          subtitle="Devices that don't need to re-enter the 2FA code for 30 days"
        />
        <div className="text-sm text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <Laptop className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
          No trusted devices yet — they appear here after you check "trust this device" at 2FA sign-in.
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Account" />
        <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200">
              Delete this user account
            </p>
            <p className="text-sm text-rose-800 dark:text-rose-300 mt-0.5">
              Closes your account and signs you out everywhere. You must transfer or close any business you own first.
            </p>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>Delete</Button>
        </div>
      </Card>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => { signOut(); window.location.href = '/login'; }}
      />

      <TwoFaWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={(f) => {
          handleAddFactor(f);
          setWizardOpen(false);
        }}
      />

      <DisableTwoFaModal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onConfirm={handleDisable}
      />
    </div>
  );
}

const KIND_LABEL: Record<MfaKind, string> = {
  sms: 'SMS to your mobile',
  email: 'Email to your address',
  totp_authy: 'Authy (TOTP)',
  totp_google: 'Google Authenticator (TOTP)',
};

function TwoFaDisabledView() {
  return (
    <div className="flex items-start gap-4 flex-col sm:flex-row">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            Two-factor authentication
          </h3>
          <span className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
            Status:
            <span className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-300">
              <CircleDot className="w-3 h-3" /> Coming soon
            </span>
          </span>
        </div>
        <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
          Two-factor authentication isn't available yet. We'll enable SMS, email, and authenticator-app (TOTP) options here soon.
        </p>
        <Button disabled title="Two-factor authentication is coming soon" iconLeft={<ShieldCheck className="w-4 h-4" />}>
          Enable 2FA (coming soon)
        </Button>
      </div>
    </div>
  );
}

function TwoFaEnabledView({
  factors,
  onAddAnother,
  onDisable,
}: {
  factors: MfaFactor[];
  onAddAnother: () => void;
  onDisable: () => void;
}) {
  const primary = factors.find((f) => f.isPrimary) ?? factors[0]!;
  const [regenOpen, setRegenOpen] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
          Two-factor authentication
        </h3>
        <Badge tone="success">Enabled</Badge>
      </div>
      <dl className="text-sm space-y-1.5 mb-4">
        <div className="flex gap-3">
          <dt className="text-zinc-500 dark:text-zinc-400 w-28">Primary</dt>
          <dd className="text-zinc-900 dark:text-zinc-100">{KIND_LABEL[primary.kind]}{primary.verifiedAt ? ` (added ${primary.verifiedAt})` : ''}</dd>
        </div>
        <div className="flex gap-3">
          <dt className="text-zinc-500 dark:text-zinc-400 w-28">Last used</dt>
          <dd className="text-zinc-900 dark:text-zinc-100">{primary.lastUsedAt || 'never'}</dd>
        </div>
        {factors.length > 1 && (
          <div className="flex gap-3">
            <dt className="text-zinc-500 dark:text-zinc-400 w-28">Backup methods</dt>
            <dd className="text-zinc-900 dark:text-zinc-100">
              {factors.filter((f) => !f.isPrimary).map((f) => KIND_LABEL[f.kind]).join(', ')}
            </dd>
          </div>
        )}
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" iconLeft={<Plus className="w-3.5 h-3.5" />} onClick={onAddAnother}>
          Add another method
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<Key className="w-3.5 h-3.5" />} onClick={() => setRegenOpen(true)}>
          Regenerate recovery codes
        </Button>
        <Button variant="ghost" size="sm" iconLeft={<ShieldOff className="w-3.5 h-3.5" />} onClick={onDisable}>
          Disable 2FA
        </Button>
      </div>

      <Modal
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        title="Regenerate recovery codes"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRegenOpen(false)}>Cancel</Button>
            <Button onClick={() => setRegenOpen(false)}>Regenerate</Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          This will invalidate your current recovery codes and generate 10 new ones. Make sure to save the new codes somewhere safe.
        </p>
      </Modal>
    </div>
  );
}

function DisableTwoFaModal({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [pwd, setPwd] = useState('');

  return (
    <Modal
      open={open}
      onClose={() => {
        setPwd('');
        onClose();
      }}
      title="Disable two-factor authentication"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => { setPwd(''); onClose(); }}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={pwd.trim().length === 0}
            onClick={() => {
              setPwd('');
              onConfirm();
            }}
          >
            Disable 2FA
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Your account will only be protected by your password after disabling 2FA.</span>
        </div>
        <Input
          label="Confirm with your password"
          type="password"
          required
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
        />
      </div>
    </Modal>
  );
}

/* ── Change password ─────────────────────────────────────────────── */

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setDone(false);
  }
  function close() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      if (!USE_MOCKS) await apiPost('/me/password', { currentPassword: current, newPassword: next });
      setDone(true);
      setTimeout(close, 1200);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 401 ? 'Current password is incorrect.' : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Change password"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy} disabled={busy || !current || !next || !confirm}>
            Update password
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {done && <div className="text-sm text-emerald-700 dark:text-emerald-400">Password updated ✓</div>}
        <Input label="Current password" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" />
        <Input label="New password" type="password" required value={next} onChange={(e) => setNext(e.target.value)} hint="At least 8 characters" placeholder="••••••••" />
        <Input label="Confirm new password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
      </div>
    </Modal>
  );
}

/* ── Delete account ──────────────────────────────────────────────── */

function DeleteAccountModal({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = confirm.trim().toUpperCase() === 'DELETE';

  function close() {
    setConfirm('');
    setError(null);
    onClose();
  }

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      if (!USE_MOCKS) await apiDelete('/me');
      onDeleted();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? 'You still own an active business. Transfer or close it before deleting your account.' : 'Could not delete the account.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Delete account"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} loading={busy} disabled={!valid || busy}>Delete my account</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
        )}
        <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-3 text-sm text-rose-900 dark:text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>This closes your account and signs you out everywhere. You must transfer or close any business you own first.</span>
        </div>
        <Input label="Type DELETE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" />
      </div>
    </Modal>
  );
}

/* ── 2FA wizard ──────────────────────────────────────────────────── */

function TwoFaWizard({
  open,
  onClose,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  onComplete: (f: MfaFactor) => void;
}) {
  const { data: my2faRecoveryCodes = [] } = useMy2faRecoveryCodes();
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState<MfaKind>('totp_authy');
  const [code, setCode] = useState('');
  const [savedAck, setSavedAck] = useState(false);

  function reset() {
    setStep(1);
    setKind('totp_authy');
    setCode('');
    setSavedAck(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const isTotp = kind === 'totp_authy' || kind === 'totp_google';
  const codeLen = isTotp ? 6 : 4;
  const codeIsValid = isTotp ? /^\d{6}$/.test(code) : /^\d{4}$/.test(code);

  function next() {
    if (step === 1) {
      // For non-TOTP, skip the QR step
      setStep(isTotp ? 2 : 3);
      return;
    }
    if (step === 2) {
      setStep(3);
      return;
    }
    if (step === 3) {
      setStep(4);
      return;
    }
    if (step === 4) {
      const newFactor: MfaFactor = {
        id: `mfa_${Math.random().toString(36).slice(2, 8)}`,
        kind,
        label: KIND_LABEL[kind],
        isPrimary: false,
        verifiedAt: 'just now',
        lastUsedAt: 'never',
      };
      onComplete(newFactor);
      reset();
    }
  }

  function back() {
    if (step === 4) {
      setStep(3);
      return;
    }
    if (step === 3) {
      setStep(isTotp ? 2 : 1);
      return;
    }
    if (step === 2) {
      setStep(1);
      return;
    }
  }

  const totalVisibleSteps = isTotp ? 4 : 3;
  const visibleStepIndex = step === 1 ? 1 : step === 2 ? 2 : step === 3 ? (isTotp ? 3 : 2) : (isTotp ? 4 : 3);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Enable two-factor authentication"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          {step > 1 && (
            <Button variant="secondary" onClick={back}>Back</Button>
          )}
          <Button
            onClick={next}
            disabled={
              (step === 3 && !codeIsValid) ||
              (step === 4 && !savedAck)
            }
          >
            {step === 4 ? 'Finish' : 'Next'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          Step {visibleStepIndex} of {totalVisibleSteps}
        </div>

        {step === 1 && (
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Choose your primary 2FA method
            </h3>
            <div className="space-y-2">
              {(['sms', 'email', 'totp_authy', 'totp_google'] as MfaKind[]).map((k) => (
                <label
                  key={k}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-md border cursor-pointer transition',
                    kind === k
                      ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                      : 'border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/70',
                  )}
                >
                  <input
                    type="radio"
                    name="mfa-kind"
                    checked={kind === k}
                    onChange={() => setKind(k)}
                    className="mt-1 text-brand focus:ring-brand"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                      {KIND_LABEL[k]}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                      {k === 'sms' && 'A 4-digit code is sent to your verified mobile.'}
                      {k === 'email' && 'A 4-digit code is sent to your verified email.'}
                      {k === 'totp_authy' && 'Use Authy on your phone to generate a 6-digit code every 30s.'}
                      {k === 'totp_google' && 'Use Google Authenticator to generate a 6-digit code every 30s.'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 2 && isTotp && (
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Scan this QR code
            </h3>
            <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start">
              <div className="w-40 h-40 rounded-md bg-white border-2 border-zinc-300 dark:bg-zinc-100 dark:border-zinc-300 grid place-items-center">
                <QrCode className="w-24 h-24 text-zinc-900" />
              </div>
              <div className="text-sm text-zinc-700 dark:text-zinc-200 space-y-3">
                <p>
                  Open <strong>{kind === 'totp_authy' ? 'Authy' : 'Google Authenticator'}</strong> on your phone and scan the QR code, or enter the key manually:
                </p>
                <div className="rounded-md bg-zinc-100 dark:bg-zinc-800 px-3 py-2 mono text-sm tabular-nums select-all">
                  JBSWY3DPEHPK3PXP
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Once you have a 6-digit code in the app, click Next to verify.
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Enter the {codeLen}-digit code
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
              {isTotp
                ? `Type the ${codeLen}-digit code currently shown in your authenticator app.`
                : `We just sent a ${codeLen}-digit code to your ${kind === 'sms' ? 'mobile' : 'email'}. Enter it below.`}
            </p>
            <Input
              label={`Verification code (${codeLen} digits)`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, codeLen))}
              placeholder={'•'.repeat(codeLen)}
              autoFocus
              inputMode="numeric"
              className="tabular-nums tracking-widest"
            />
          </div>
        )}

        {step === 4 && (
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              Save your recovery codes
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
              If you lose access to your second factor, these single-use codes will get you back in. Store them somewhere safe (password manager, printed copy).
            </p>
            <div className="rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4 grid grid-cols-2 gap-x-4 gap-y-1.5 mono text-sm tabular-nums">
              {my2faRecoveryCodes.map((c) => (
                <span key={c} className="text-zinc-800 dark:text-zinc-100">{c}</span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Copy className="w-3.5 h-3.5" />}
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard) {
                    navigator.clipboard.writeText(my2faRecoveryCodes.join('\n')).catch(() => undefined);
                  }
                }}
              >
                Copy all
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Printer className="w-3.5 h-3.5" />}
                onClick={() => {
                  if (typeof window !== 'undefined') window.print();
                }}
              >
                Print
              </Button>
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={savedAck}
                onChange={(e) => setSavedAck(e.target.checked)}
                className="text-brand focus:ring-brand"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-200">
                I've saved my recovery codes somewhere safe.
              </span>
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ── Sessions tab ───────────────────────────────────────────────── */

function SessionIcon({ kind }: { kind: DeviceSession['deviceIcon'] }) {
  const cls = 'w-4 h-4';
  if (kind === 'laptop') return <Laptop className={cls} />;
  if (kind === 'monitor') return <Monitor className={cls} />;
  if (kind === 'smartphone') return <Smartphone className={cls} />;
  return <Tablet className={cls} />;
}

function SessionsTab({
  sessions,
  setSessions,
}: {
  sessions: DeviceSession[];
  setSessions: React.Dispatch<React.SetStateAction<DeviceSession[]>>;
}) {
  const brand = useBrand();
  const activeCount = useMemo(() => sessions.filter((s) => !s.revokedAt).length, [sessions]);
  const [confirmRevoke, setConfirmRevoke] = useState<DeviceSession | null>(null);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [busy, setBusy] = useState(false);

  async function revokeSession(id: string) {
    setBusy(true);
    try {
      await revokeMySession(id);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, revokedAt: new Date().toISOString(), revokedReason: 'user_signed_out' } : s,
        ),
      );
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not revoke the session.');
    } finally {
      setBusy(false);
      setConfirmRevoke(null);
    }
  }

  // The token doesn't identify which row is "this device", so the server revokes
  // EVERYTHING (sessions + refresh tokens); this signed-in tab lives only until
  // its short-lived access token expires.
  async function revokeAllOthers() {
    setBusy(true);
    try {
      await revokeAllMySessions();
      setSessions((prev) =>
        prev.map((s) => (s.revokedAt ? s : { ...s, revokedAt: new Date().toISOString(), revokedReason: 'user_signed_out' })),
      );
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not revoke the sessions.');
    } finally {
      setBusy(false);
      setConfirmRevokeAll(false);
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b border-zinc-200 dark:border-zinc-700">
        <div>
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            Active sessions ({activeCount} of {SESSION_CAP_DEFAULT} device cap)
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">
            Devices currently signed in to your {brand.name} account.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirmRevokeAll(true)}
          disabled={sessions.filter((s) => !s.isCurrent && !s.revokedAt).length === 0}
        >
          Revoke all others
        </Button>
      </div>

      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Device</th>
              <th className="text-left font-medium px-4 py-3">Location</th>
              <th className="text-left font-medium px-4 py-3">Last active</th>
              <th className="text-right font-medium px-5 sm:px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {sessions.map((s) => (
              <tr key={s.id} className={cn('transition', s.revokedAt ? 'opacity-60' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/70')}>
                <td className="px-5 sm:px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-md bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400">
                      <SessionIcon kind={s.deviceIcon} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{s.deviceLabel}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{s.userAgent.length > 50 ? s.userAgent.slice(0, 50) + '…' : s.userAgent}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 tabular-nums">
                  {s.geoCity}, {s.geoCountry}
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{s.lastActiveAt}</td>
                <td className="px-5 sm:px-6 py-3 text-right">
                  {s.revokedAt ? (
                    <span className="text-xs text-zinc-400 dark:text-zinc-500">Revoked</span>
                  ) : s.isCurrent ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400">
                      <span aria-hidden>★</span> Current
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(s)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 sm:px-6 py-4 bg-zinc-50/50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-700">
        <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          Revoking a session immediately invalidates that device's refresh token.
        </p>
      </div>

      <Modal
        open={confirmRevoke !== null}
        onClose={() => !busy && setConfirmRevoke(null)}
        title="Revoke session"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)} disabled={busy}>Cancel</Button>
            <Button
              variant="destructive"
              loading={busy}
              onClick={() => confirmRevoke && revokeSession(confirmRevoke.id)}
            >
              Sign out from {confirmRevoke?.deviceLabel}
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Sign out from <strong>{confirmRevoke?.deviceLabel}</strong>? This will end the session immediately and the device will need to log in again.
        </p>
      </Modal>

      <Modal
        open={confirmRevokeAll}
        onClose={() => !busy && setConfirmRevokeAll(false)}
        title="Revoke all other sessions"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevokeAll(false)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" loading={busy} onClick={revokeAllOthers}>Revoke all others</Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          You'll stay signed in on this device. All other active sessions will be ended.
        </p>
      </Modal>
    </Card>
  );
}

/* ── Notifications preferences (unchanged feature) ─────────────────── */

/** Canonical opt-in list — stable keys persist to users.notification_prefs. */
const NOTIF_GROUPS: Array<{ title: string; items: Array<{ key: string; label: string; defaultOn: boolean }> }> = [
  {
    title: 'Billing',
    items: [
      { key: 'payment_receipts', label: 'Payment receipts', defaultOn: true },
      { key: 'payment_failures', label: 'Payment failures', defaultOn: true },
      { key: 'dunning_reminders', label: 'Dunning reminders (when overdue)', defaultOn: true },
      { key: 'trial_ending', label: 'Trial ending in 3 days', defaultOn: true },
      { key: 'plan_changes', label: 'Plan changes I make', defaultOn: true },
      { key: 'price_change_notice', label: 'Price-change advance notice (60 days before)', defaultOn: false },
    ],
  },
  {
    title: 'Account & access',
    items: [
      { key: 'new_device_signin', label: 'New device sign-in', defaultOn: true },
      { key: 'credential_changes', label: 'Password / email / 2FA changes', defaultOn: true },
      { key: 'team_changes', label: 'Team member added or removed', defaultOn: true },
    ],
  },
  {
    title: 'Operational',
    items: [
      { key: 'daily_pos_summary', label: 'Daily POS summary', defaultOn: false },
      { key: 'weekly_revenue_report', label: 'Weekly revenue report', defaultOn: false },
      { key: 'backup_completed', label: 'Database backup-completed confirmation', defaultOn: false },
    ],
  },
];

const NOTIF_DEFAULTS: Record<string, boolean> = Object.fromEntries(
  NOTIF_GROUPS.flatMap((g) => g.items.map((it) => [it.key, it.defaultOn])),
);

function NotificationsTab() {
  const [prefs, setPrefs] = useState<Record<string, boolean>>(NOTIF_DEFAULTS);
  const [loaded, setLoaded] = useState<Record<string, boolean>>(NOTIF_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = useMemo(() => NOTIF_GROUPS.some((g) => g.items.some((it) => prefs[it.key] !== loaded[it.key])), [prefs, loaded]);

  useEffect(() => {
    if (USE_MOCKS) return;
    let alive = true;
    apiGet<Record<string, boolean>>('/me/notification-preferences')
      .then((server) => {
        if (!alive) return;
        const merged = { ...NOTIF_DEFAULTS, ...server };
        setPrefs(merged);
        setLoaded(merged);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      if (!USE_MOCKS) await apiPatch('/me/notification-preferences', { prefs });
      setLoaded(prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {NOTIF_GROUPS.map((g) => (
        <NotifGroup
          key={g.title}
          title={g.title}
          items={g.items.map((it) => ({ key: it.key, label: it.label, value: prefs[it.key] ?? it.defaultOn }))}
          onToggle={(key, value) => setPrefs((p) => ({ ...p, [key]: value }))}
        />
      ))}

      <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
          Required (cannot opt out)
        </div>
        <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-200">
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Account security alerts</li>
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Legal / Terms updates</li>
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Refund / chargeback notices</li>
        </ul>
      </div>

      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={() => setPrefs(loaded)} disabled={busy || !dirty}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy || !dirty}>Save changes</Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function SelectFake({ label, value, options }: { label: string; value: string; options: string[] }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">{label}</span>
      <select
        defaultValue={value}
        className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50
                   border border-zinc-300 dark:border-zinc-700
                   focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand
                   dark:focus:ring-brand-dm dark:focus:border-brand-dm transition"
      >
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function DeviceRow({
  icon,
  name,
  loc,
  lastSeen,
  trusted = false,
}: {
  icon: React.ReactNode;
  name: string;
  loc: string;
  lastSeen: string;
  trusted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-md bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{name}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          {loc} · last seen {lastSeen}
        </div>
      </div>
      {trusted && <Badge tone="success">Trusted</Badge>}
    </div>
  );
}

function NotifGroup({
  title,
  items,
  onToggle,
}: {
  title: string;
  items: Array<{ key: string; label: string; value: boolean }>;
  onToggle: (key: string, value: boolean) => void;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
        {title}
      </div>
      <Card className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {items.map((it) => (
          <ToggleRow key={it.key} label={it.label} value={it.value} onChange={(v) => onToggle(it.key, v)} />
        ))}
      </Card>
    </div>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 px-5 sm:px-6 py-3 cursor-pointer">
      <span className="text-sm text-zinc-800 dark:text-zinc-100">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={cn(
          'relative w-9 h-5 rounded-full transition-colors',
          value ? 'bg-brand dark:bg-brand-dm' : 'bg-zinc-200 dark:bg-zinc-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform',
            value ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}
